import React, { useState, useEffect } from "react";
import {
  Dialog,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { manageUsersQueryOptions } from "../../../queryOptions/ManageBookingsOptions";
import { User } from "../../../schemas/user";
import { getMembershipListing } from "../../../apis/membershipApi";
import { MembershipListing } from "../../../schemas/memberships";
import { toast } from "react-toastify";

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  border: "1px solid #e9ecef",
  boxShadow: "none",
}));

const SummaryRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(1),
  "& .label": {
    fontWeight: 500,
    color: "#495057",
  },
  "& .value": {
    color: "#212529",
  },
}));

interface AddMembershipFormProps {
  open: boolean;
  onClose: () => void;
  onAddMembership: (membershipData: any) => void;
  membershipListing: { id: string; name: string; amount: number }[];
}

const AddMembershipForm: React.FC<AddMembershipFormProps> = ({
  open,
  onClose,
  onAddMembership,
}) => {
  // User details
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [taxRate, setTaxRate] = useState(14);
  const { data: userData, refetch: userRefetch } = useQuery(
    manageUsersQueryOptions()
  );
  const membershipPlansQuery = useQuery({
    queryKey: ["membershipPlans"],
    queryFn: getMembershipListing,
  });
  const { data: listingData, refetch: listingRefetch } = membershipPlansQuery;

  const [selectedUser, setSelectedUser] = useState<{
    label: string;
    id: string;
  } | null>(null);
  const [selectedMembership, setSelectedMembership] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("checkout");
  const [isLoading, setIsLoading] = useState(false);
  const [discountPercent, setDiscountPercent] = useState<number>(0);
  const [discountDurationMonths, setDiscountDurationMonths] = useState<number>(0);

  const handleUserSelect = (newValue: { label: string; id: string } | null) => {
    setSelectedUser(newValue);
    if (newValue) {
      const user = userData?.find((user: User) => user.id === newValue.id);
      if (user) {
        setFirstName(user.firstName);
        setLastName(user.lastName);
        setEmail(user.email);
        setPhone(user.phoneNumber);
        setAddress(user.address);
      }
    } else {
      // Clear form if no user selected
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setAddress("");
      setSelectedUser(null);
    }
  };

  const selectedMembershipData =
    listingData &&
    listingData.find(
      (plan: MembershipListing) => plan.id === selectedMembership
    );
  const baseSubtotal = selectedMembershipData?.amount || 0;
  const discountAmount = discountPercent > 0 ? (baseSubtotal * discountPercent) / 100 : 0;
  const subtotal = baseSubtotal - discountAmount;
  const taxAmount = (subtotal * taxRate) / 100;
  const totalAmount = subtotal + taxAmount;

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const membershipData = {
        userDetails: {
          id: selectedUser?.id || "",
          firstName: firstName || "",
          lastName: lastName || "",
          email: email || "",
          phone: phone || "",
          address: address || "",
          specialRequests: specialRequests || "",
        },
        membershipListingId: selectedMembership,
        paymentStatus,
        amount: totalAmount,
        ...(discountPercent > 0 && discountDurationMonths > 0 ? {
          discountPercent,
          discountDurationMonths
        } : {})
      };

      await onAddMembership(membershipData);
      handleReset();
      onClose();
    } catch (error) {
      console.error("Error submitting membership data:", error);
      toast.error("Failed to create membership. Please try again.");
      onClose();
    } finally {
      // Refetch user and membership listings after submission
      setIsLoading(false);
      userRefetch();
      listingRefetch();
    }
  };

  const handleReset = () => {
    setSelectedUser(null);
    setSelectedMembership("");
    setPaymentStatus("checkout");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setSpecialRequests("");
    setDiscountPercent(0);
    setDiscountDurationMonths(0);
  };

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      resetForm();
      userRefetch();
    }
  }, [open, userRefetch]);
  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setAddress("");
    setSpecialRequests("");
    setPaymentStatus("checkout");
    setDiscountPercent(0);
    setDiscountDurationMonths(0);
  };

  return (
    <Dialog open={open} fullWidth maxWidth="lg" onClose={onClose}>
      <Paper sx={{ padding: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Create Membership
        </Typography>

        <Grid container spacing={4}>
          {/* Left Column - Form */}
          <Grid item xs={12} md={7}>
            {/* User Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Customer Information
              </Typography>

              <Autocomplete
                disablePortal
                options={
                  userData &&
                  userData
                    .filter((user: User) => user.role === "user")
                    .map((user: User) => ({
                      label: `${user.firstName} ${user.lastName} (${user.email})`,
                      id: user.id,
                    }))
                }
                value={selectedUser}
                getOptionLabel={(option: any) => option.label || ""}
                renderInput={(params) => (
                  <TextField {...params} label="Search User" fullWidth />
                )}
                onChange={(_event, newValue) => handleUserSelect(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={{ mb: 3 }}
              />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    // disabled={!isNewUser && selectedUser !== ""}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    // disabled={!isNewUser && selectedUser !== ""}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    // disabled={!isNewUser && selectedUser !== ""}
                    required
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    type="number"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    // disabled={!isNewUser && selectedUser !== ""}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    multiline
                    rows={2}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    // disabled={!isNewUser && selectedUser !== ""}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Special Notes"
                    multiline
                    rows={2}
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    placeholder="Any special instructions or notes..."
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Membership Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Membership Plan
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Membership Plan</InputLabel>
                <Select
                  value={selectedMembership}
                  onChange={(e) => setSelectedMembership(e.target.value)}
                  label="Select Membership Plan"
                >
                  {listingData &&
                    listingData.map((plan: MembershipListing) => (
                      <MenuItem key={plan.id} value={plan.id}>
                        {plan.name} - ${plan.amount}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Discount Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Discount (Optional)
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Discount Percentage"
                    type="number"
                    value={discountPercent}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setDiscountPercent(Math.max(0, Math.min(100, value)));
                    }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    helperText="Enter discount percentage (0-100)"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Discount Duration</InputLabel>
                    <Select
                      value={discountDurationMonths}
                      onChange={(e) => setDiscountDurationMonths(Number(e.target.value))}
                      label="Discount Duration"
                      disabled={discountPercent === 0}
                    >
                      <MenuItem value={0}>No Discount</MenuItem>
                      <MenuItem value={1}>1 Month</MenuItem>
                      <MenuItem value={3}>3 Months</MenuItem>
                      <MenuItem value={6}>6 Months</MenuItem>
                      <MenuItem value={12}>1 Year</MenuItem>
                      <MenuItem value={24}>2 Years</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {discountPercent > 0 && discountDurationMonths > 0 && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
                  <Typography variant="body2" color="info.dark">
                    {discountPercent}% discount will be applied for {discountDurationMonths} month{discountDurationMonths !== 1 ? 's' : ''}. 
                    After this period, regular pricing will apply.
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Payment Method */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                Payment Method
              </Typography>

              <FormControl fullWidth>
                <InputLabel>Payment Method</InputLabel>
                <Select
                  value={paymentStatus}
                  onChange={(e) => setPaymentStatus(e.target.value)}
                  label="Payment Method"
                >
                  <MenuItem value="checkout">
                    CHECKOUT
                  </MenuItem>
                  {/* <MenuItem value="pay_at_counter">
                    PAY_CASH / PAY_AT_COUNTER
                  </MenuItem> */}
                  {/* <MenuItem value="confirmed">CONFIRMED</MenuItem> */}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          {/* Right Column - Summary */}
          <Grid item xs={12} md={5}>
            <StyledCard
              sx={{
                borderRadius: 2,
                display: { xs: "block", md: "block" },
                height: "fit-content",
                position: "sticky",
                top: 80,
                alignSelf: "flex-start",
                maxHeight: "calc(100vh - 50px)",
                overflowY: "auto",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Membership Summary
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <SummaryRow>
                    <Typography className="label">Customer:</Typography>
                    <Typography className="value">
                      {firstName && lastName
                        ? `${firstName} ${lastName}`
                        : "Not selected"}
                    </Typography>
                  </SummaryRow>

                  {email && (
                    <SummaryRow>
                      <Typography className="label">Email:</Typography>
                      <Typography className="value" sx={{ fontSize: "0.9rem" }}>
                        {email}
                      </Typography>
                    </SummaryRow>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <SummaryRow>
                    <Typography className="label">Plan:</Typography>
                    <Typography className="value">
                      {selectedMembershipData?.name || "Not selected"}
                    </Typography>
                  </SummaryRow>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <SummaryRow>
                    <Typography className="label">Status:</Typography>
                    <Chip
                      label={paymentStatus}
                      size="small"
                      sx={{
                        backgroundColor: "#6c757d",
                        color: "white",
                        fontSize: "0.75rem",
                      }}
                    />
                  </SummaryRow>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Pricing */}
                <Box sx={{ mb: 2 }}>
                  <SummaryRow>
                    <Typography className="label">Base Price:</Typography>
                    <Typography className="value">
                      CA${baseSubtotal.toFixed(2)}
                    </Typography>
                  </SummaryRow>

                  {discountPercent > 0 && discountDurationMonths > 0 && (
                    <>
                      <SummaryRow>
                        <Typography className="label" sx={{ color: "success.main" }}>
                          Discount ({discountPercent}%):
                        </Typography>
                        <Typography className="value" sx={{ color: "success.main" }}>
                          -CA${discountAmount.toFixed(2)}
                        </Typography>
                      </SummaryRow>
                      <SummaryRow>
                        <Typography className="label" sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                          Valid for {discountDurationMonths} month{discountDurationMonths !== 1 ? 's' : ''}
                        </Typography>
                      </SummaryRow>
                    </>
                  )}

                  <SummaryRow>
                    <Typography className="label">Subtotal:</Typography>
                    <Typography className="value">
                      CA${subtotal.toFixed(2)}
                    </Typography>
                  </SummaryRow>

                  <SummaryRow>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography className="label" component="span">
                        Tax Rate (HST):
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={taxRate}
                        onChange={(e) => setTaxRate(Number(e.target.value))}
                        inputProps={{ min: 0, step: 0.1 }}
                        sx={{ width: 80 }}
                      />
                      <Typography component="span" sx={{ fontSize: "0.9rem" }}>
                        %
                      </Typography>
                    </Box>
                    <Typography className="value">
                      CA${taxAmount.toFixed(2)}
                    </Typography>
                  </SummaryRow>
                </Box>

                <Divider sx={{ my: 2 }} />

                <SummaryRow sx={{ mb: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Total Amount:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    CA${totalAmount.toFixed(2)}
                  </Typography>
                </SummaryRow>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}
        >
          <Button variant="outlined" onClick={handleReset}>
            Reset
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !selectedMembership ||
              !firstName ||
              !lastName ||
              !email ||
              !paymentStatus ||
              !address
            }
          >
            {isLoading ? "Creating Membership..." : "Create Membership"}
          </Button>
        </Box>
      </Paper>
    </Dialog>
  );
};

export default AddMembershipForm;
